const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getLastThreeYears() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 3 }, (_, i) => String(currentYear - i));
}

function getCssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// Mismo criterio de color por año (variables CSS, no BAR_PALETTE por
// índice) que ya usan los 3 gráficos DualAxes en pantalla y su equivalente
// ya migrado en Efluentes — funciona igual fuera de un componente React,
// getComputedStyle solo necesita el DOM, no un ciclo de render.
function buildYearColorMap(years) {
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: getCssVar("--chart-year-oldest") || "#cbd5e1",
    [sorted[1]]: getCssVar("--chart-year-prev") || "#94a3b8",
    [sorted[2]]: getCssVar("--chart-year-last") || "#1e293b",
  };
}

// Últimos 3 años, mensual — misma llamada que hoy hace el useEffect de
// SocialComparisonChart, extraída para poder reusarla en el PDF.
export async function fetchProductionMonthlyData(productionController, accessToken, site, baseline) {
  if (!baseline || !site) return {};

  const lastThreeYears = getLastThreeYears();

  try {
    const response = await productionController.getProductionFormsBySiteAndYears(
      accessToken,
      site,
      lastThreeYears,
    );

    const collectAllArrays = (obj, arrays = []) => {
      if (Array.isArray(obj)) {
        arrays.push(...obj);
        return arrays;
      }
      if (typeof obj === "object" && obj !== null) {
        for (const key in obj) {
          collectAllArrays(obj[key], arrays);
        }
      }
      return arrays;
    };

    const dataArray = collectAllArrays(response);
    const organizedData = {};

    dataArray.forEach((item) => {
      const year = String(item.year);
      const month = item.period;
      if (!organizedData[year]) organizedData[year] = {};

      const baselineType = baseline?.[year] || "VPS";
      const fieldName =
        baselineType === "VPS" ? "standardized_production" : "standardized_billing";

      organizedData[year][month] = {
        value: item[fieldName]?.value || 0,
        type: baselineType,
      };
    });

    return organizedData;
  } catch (error) {
    console.error("❌ Error obteniendo datos de producción mensuales:", error);
    return {};
  }
}

// "Comparación producción" — barras mensuales por año + línea de promedio
// punteada. Siempre los 3 años completos (el PDF no respeta el toggle
// activeYears de la UI, igual que en todos los dashboards anteriores).
export function buildProductionComparisonData(monthlyProductionData, baseline, t) {
  const years = getLastThreeYears();
  const bar = [];
  const line = [];

  years.forEach((year) => {
    const yearData = monthlyProductionData[year];
    let total = 0;
    let monthsWithData = 0;

    if (yearData) {
      MONTHS.forEach((month) => {
        const monthData = yearData[month];
        if (monthData && monthData.value > 0) {
          total += monthData.value;
          monthsWithData++;
        }
      });
    }

    const average = monthsWithData > 0 ? total / monthsWithData : 0;

    MONTHS.forEach((month) => {
      const monthData = yearData?.[month];
      const value = monthData?.value || 0;
      const type = monthData?.type || baseline?.[year] || "VPS";

      bar.push({ year: String(year), month: t(month), value, type, isAverage: false });
      line.push({ year: String(year), month: t(month), value: average, type, isAverage: true });
    });
  });

  return { years, barData: bar, lineData: line, yearColorMap: buildYearColorMap(years) };
}

// KPISoc1 (cantidad total de trabajadores) por mes — misma fórmula que
// SocialMonthlyChart en pantalla.
export function calculateWorkersPerMonth(monthData) {
  if (!monthData) return 0;

  const productionWorkers =
    (monthData.female_production_workers?.value || 0) +
    (monthData.male_production_workers?.value || 0) +
    (monthData.other_gender_production_workers?.value || 0);

  const administrativeWorkers =
    (monthData.female_administrative_workers?.value || 0) +
    (monthData.male_administrative_workers?.value || 0) +
    (monthData.administrative_workers_other_genders?.value || 0);

  const temporaryWorkers =
    (monthData.female_temporary_workers?.value || 0) +
    (monthData.male_temporary_workers?.value || 0) +
    (monthData.temporary_workers_other_genders?.value || 0);

  const leadershipWorkers =
    (monthData.female_workers_leadership_positions?.value || 0) +
    (monthData.male_workers_leadership_positions?.value || 0);

  return productionWorkers + administrativeWorkers + temporaryWorkers + leadershipWorkers;
}

// "Comparación trabajadores" — barras mensuales por año + línea de
// promedio punteada. Puro (sin fetch), usa totalesByFilters tal cual ya
// está disponible en el dashboard padre.
export function buildWorkersComparisonData(resultsByYear, t) {
  const years = getLastThreeYears();
  const bar = [];
  const line = [];

  years.forEach((year) => {
    let total = 0;
    let count = 0;
    const yearBarData = [];

    MONTHS.forEach((month) => {
      const monthData = resultsByYear?.[year]?.[month];
      const workersCount = calculateWorkersPerMonth(monthData);

      if (workersCount > 0) {
        yearBarData.push({ year: String(year), month: t(month), value: workersCount, type: `Total ${year}` });
        total += workersCount;
        count++;
      }
    });

    bar.push(...yearBarData);

    const avg = count ? total / count : 0;
    if (avg > 0) {
      MONTHS.forEach((month) => {
        line.push({ year: String(year), month: t(month), value: avg, type: `Promedio ${year}` });
      });
    }
  });

  return { years, barData: bar, lineData: line, yearColorMap: buildYearColorMap(years) };
}

// "Comparación productividad" — a diferencia de los otros 2, NO hay barra:
// 2 líneas (productividad real sólida + promedio punteado). Puro, usa
// totalesWorkersByFilters/totalesProductionsByFilters ya disponibles en el
// dashboard padre.
export function buildProductivityData(baseline, resultsWorkers, resultsProductions, t) {
  const years = getLastThreeYears();
  const cData = [];
  const line = [];

  years.forEach((year) => {
    const productionType = baseline?.[year] || "VPS";
    let total = 0;
    let count = 0;

    MONTHS.forEach((month) => {
      const production = resultsProductions?.[year]?.[month] || 0;
      const workers = resultsWorkers?.[year]?.[month] || 0;
      const productivityValue = workers > 0 ? production / workers : 0;

      if (productivityValue > 0) {
        cData.push({
          year: String(year),
          month: t(month),
          value: Math.round(productivityValue),
          type: `${productionType} / ${t("employee")} ${year}`,
        });
        total += productivityValue;
        count++;
      }
    });

    const avg = count ? total / count : 0;
    if (avg > 0) {
      MONTHS.forEach((month) => {
        line.push({ year: String(year), month: t(month), value: Math.round(avg), type: `Promedio ${year}` });
      });
    }
  });

  return { years, consumptionData: cData, lineData: line, yearColorMap: buildYearColorMap(years) };
}
